import { Image } from 'expo-image';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import { Box, Typography } from 'components/ui';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

const heroImage = require('./walmart-header-hero-v2.png');
const walmartPlusLogo = require('./walmart-plus-logo.svg');

interface WalmartLandingHeaderV2Props {
  isWalmartLinked?: boolean;
}

export const WalmartLandingHeaderV2 = ({
  isWalmartLinked = false,
}: WalmartLandingHeaderV2Props) => {
  const isMobile = useIsMobileBreakpoint();
  const { featureLoyaltyUiLoading, featureLoyaltyUi } = useFeatureLoyaltyUi();

  if (featureLoyaltyUiLoading) {
    return null;
  }

  const royalLogo = featureLoyaltyUi?.rewardsLogo?.locale;

  return (
    <Box style={[styles.container, isMobile && styles.mobileContainer]}>
      <Image
        source={heroImage}
        contentFit="contain"
        contentPosition={{ bottom: 0, left: '50%' }}
        style={[styles.hero, isMobile && styles.mobileHero]}
        accessibilityLabel="Burger King burger"
      />
      <Box style={[styles.copy, isMobile && styles.mobileCopy]}>
        <Typography
          variant="headerOne"
          style={[styles.title, isMobile && styles.mobileTitle]}
          bold
          accessibilityRole="header"
        >
          {isWalmartLinked
            ? t('walmartHeaderTitleLinked', 'Enjoy your BK\nsavings with\nWalmart+')
            : t('walmartHeaderTitle', 'Ready for even \nmore savings?')}
        </Typography>
        <Typography
          variant="copyTwo"
          fontSize={isMobile ? 'lg' : 'xl'}
          style={[styles.description, isMobile && styles.mobileDescription]}
        >
          {isWalmartLinked
            ? t('walmartHeaderDescriptionLinked', {
                defaultValue:
                  'Head to the Offers page to start{mobileSeparator}saving{desktopSeparator}with your exclusive{mobileSeparator}Walmart+ benefits.',
                mobileSeparator: !isMobile ? ' ' : '\n',
                desktopSeparator: isMobile ? ' ' : '\n',
              })
            : t('walmartHeaderDescription', {
                defaultValue:
                  'Link Royal Perks and Walmart+ {accounts}\nto unlock even more exclusive \nBurger King deals.',
                accounts: isMobile ? '' : 'accounts',
              })}
        </Typography>
        <Box style={[styles.logos, isMobile && styles.mobileLogos]}>
          <Picture
            image={royalLogo}
            alt="Royal Perks"
            objectFitContain
            lazy={false}
            width={isMobile ? '119px' : '164px'}
          />
          <Box style={[styles.logoDivider, isMobile && styles.mobileLogoDivider]} />
          <Image
            source={walmartPlusLogo}
            style={isMobile ? styles.mobileWalmartPlusLogo : styles.walmartPlusLogo}
            contentFit="contain"
            alt="Walmart+"
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'center',
    backgroundColor: primitive.$whiteOpacity50,
    borderRadius: spacing.$4,
    overflow: 'hidden',
    paddingHorizontal: spacing.$9,
    paddingVertical: spacing.$8,
    width: '100%',
  },
  copy: { maxWidth: '60%', position: 'relative', zIndex: 1 },
  title: {
    color: primitive.$bbqBrown,
    fontSize: 60,
    lineHeight: spacing.$16,
    marginBottom: spacing.$6,
  },
  description: { color: primitive.$bbqBrown, lineHeight: 26 },
  hero: { bottom: 0, height: '100%', position: 'absolute', right: 0, width: '50%' },
  logos: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: spacing.$11,
  },
  logoDivider: {
    borderColor: primitive.$blackOpacity20,
    borderLeftWidth: 2,
    height: 40,
    marginHorizontal: spacing.$5,
  },
  mobileContainer: {
    paddingHorizontal: spacing.$6,
    paddingVertical: spacing.$5,
    borderRadius: 0,
  },
  mobileCopy: { maxWidth: '100%' },
  mobileTitle: { fontSize: 39, lineHeight: spacing.$12, marginBottom: spacing.$4_5 },
  mobileDescription: { lineHeight: 23 },
  mobileHero: { bottom: 0, height: '100%', right: 0, width: '56%' },
  walmartPlusLogo: { width: 135, height: 46 },
  mobileWalmartPlusLogo: { width: 101, height: 34 },
  mobileLogos: { alignItems: 'flex-start', flexDirection: 'column', marginTop: 50 },
  mobileLogoDivider: {
    borderLeftWidth: 0,
    borderTopWidth: 1,
    height: 0,
    marginHorizontal: 0,
    marginVertical: spacing.$2_5,
    width: 119,
    flex: 1,
  },
});
